import { useEffect, useState } from 'react';
import { Alert, Button, Card, SectionHeading, Spinner } from './ui.js';
import { Mascot } from './Mascot.js';
import { CATEGORY_LABEL, helpApi, type HelpConfig } from './helpGrowthApi.js';

/**
 * Help & Support: one simple form to ROI Pros.
 *
 * Screenshots are encouraged and optional. The person sees, before sending, exactly which
 * technical details go with the request, and can leave them out. Nothing is described as
 * sent unless ROI Pros' intake accepted it.
 */
export function HelpSupport() {
  const [config, setConfig] = useState<HelpConfig | null>(null);
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [includeDiagnostics, setIncludeDiagnostics] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => { helpApi.config().then(setConfig).catch(() => setConfig(null)); }, []);
  if (!config) return <Spinner />;

  const max = config.maxScreenshots;
  const tooBig = files.find((f) => f.size > 5 * 1024 * 1024);
  const ready = category && subject.trim().length >= 3 && description.trim().length >= 10 && !tooBig && files.length <= max;
  const browser = {
    page: window.location.pathname.replace(/[^A-Za-z0-9/_-]/g, '') || '/',
    userAgent: navigator.userAgent.slice(0, 300),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const started = await helpApi.startRequest({ category, subject, description, includeDiagnostics, ...(includeDiagnostics ? { browser } : {}) });
      for (const f of files) await helpApi.addScreenshot(started.id, f);
      const done = await helpApi.submit(started.id);
      setResult(done.routed
        ? { tone: 'success', text: 'Sent to the ROI Pros support team. We will reply to the email on your account.' }
        : done.status === 'received'
          ? { tone: 'info', text: 'Saved, but not sent: support requests cannot be delivered from here yet. Please contact ROI Pros directly.' }
          : { tone: 'error', text: 'Saved, but it could not be delivered just now. Please try again later or contact ROI Pros directly.' });
      if (done.routed) { setSubject(''); setDescription(''); setFiles([]); setCategory(''); }
    } catch (e) {
      setResult({ tone: 'error', text: (e as Error).message || 'That could not be sent.' });
    } finally { setBusy(false); }
  };

  const input = 'mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-2 text-base sm:text-sm';
  return (
    <div className="space-y-4" data-help-support="">
      <div className="flex items-center gap-3">
        <Mascot pose="thinking" size={72} />
        <SectionHeading title="Help & Support" hint="Tell us what happened. Screenshots help us see it the way you did." />
      </div>
      {!config.routingAvailable ? (
        <Alert tone="info">Support requests can be written here, but cannot be delivered from here yet. They are saved, and this page will say so.</Alert>
      ) : null}
      <Card className="space-y-3 p-4 sm:p-6">
        <label className="block text-sm"><span className="text-slate-600">What is it about?</span>
          <select className={input} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Choose a topic</option>
            {config.categories.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] ?? c}</option>)}
          </select></label>
        <label className="block text-sm"><span className="text-slate-600">Subject</span>
          <input className={input} value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)} /></label>
        <label className="block text-sm"><span className="text-slate-600">What happened, and what did you expect?</span>
          <textarea className="mt-1 min-h-32 w-full min-w-0 rounded-md border border-slate-300 p-2 text-base sm:text-sm" value={description} maxLength={5000} onChange={(e) => setDescription(e.target.value)} /></label>

        <div className="text-sm">
          <span className="text-slate-600">Screenshots (optional, up to {max})</span>
          <p className="mt-1 rounded-md bg-amber-50 p-2 text-xs text-amber-900" data-sensitive-warning="">
            Please do not include passwords, API keys, card numbers or other sensitive information in your message or screenshots. Crop or cover them first.
          </p>
          <input className="mt-2 block w-full text-sm" type="file" accept="image/png,image/jpeg,image/webp" multiple
            onChange={(e) => setFiles([...(e.target.files ?? [])].slice(0, max))} />
          {files.length > 0 ? <p className="mt-1 text-xs text-slate-500">{files.length} attached. Location and camera details are removed before saving.</p> : null}
          {tooBig ? <p className="mt-1 text-xs text-red-700">Each screenshot must be 5 MB or less.</p> : null}
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={includeDiagnostics} onChange={(e) => setIncludeDiagnostics(e.target.checked)} />
          <span>Include technical details: app version, your role, which connections are on, how many destinations are active, this page, your browser and screen size. No passwords, keys or contacts.</span>
        </label>

        {result ? <Alert tone={result.tone}>{result.text}</Alert> : null}
        <Button className="min-h-11" disabled={!ready || busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send to ROI Pros'}</Button>
      </Card>
    </div>
  );
}
